/**
 * Feature flag access.
 *
 * Two layers gate every continuing-review surface:
 *  1. REACT_APP_ENABLE_CONTINUING_REVIEWS — a build-time presentation guard.
 *     It only hides UI; it is NOT a security control.
 *  2. Server-side rows in public.feature_flags — checked again inside every
 *     RPC and edge function, so direct API calls cannot bypass rollout state.
 *
 * The frontend reads the server flags to decide what to render; the server
 * re-checks them on every privileged action.
 */

import { TypedSupabaseClient } from '../lib/supabaseClient';

export const CONTINUING_REVIEW_FLAGS = {
  adminQueue: 'continuing_reviews_admin_queue_enabled',
  userCta: 'continuing_reviews_user_cta_enabled',
  creation: 'continuing_reviews_creation_enabled',
  reminders: 'continuing_reviews_reminders_enabled',
  billing: 'continuing_reviews_billing_enabled',
} as const;

/** Build-time presentation guard. Default off unless explicitly 'true'. */
export const isContinuingReviewsUiEnabled = (): boolean =>
  process.env.REACT_APP_ENABLE_CONTINUING_REVIEWS === 'true';

/** Check one server-side flag. Fails closed (false) on any error. */
export const isFeatureEnabled = async (
  supabase: TypedSupabaseClient,
  key: string
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('is_feature_enabled', { p_key: key });
    if (error) {
      console.error(`Error checking feature flag ${key}:`, error);
      return false;
    }
    return data === true;
  } catch (err) {
    console.error(`Unexpected error checking feature flag ${key}:`, err);
    return false;
  }
};

export interface ContinuingReviewFlagState {
  adminQueue: boolean;
  userCta: boolean;
  creation: boolean;
  reminders: boolean;
  billing: boolean;
}

/** Fetch all continuing-review server flags in one query. Fails closed. */
export const getContinuingReviewFlags = async (
  supabase: TypedSupabaseClient
): Promise<ContinuingReviewFlagState> => {
  const closed: ContinuingReviewFlagState = {
    adminQueue: false,
    userCta: false,
    creation: false,
    reminders: false,
    billing: false,
  };

  try {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('key, enabled')
      .in('key', Object.values(CONTINUING_REVIEW_FLAGS));

    if (error || !data) {
      if (error) console.error('Error loading continuing review flags:', error);
      return closed;
    }

    const byKey = new Map(data.map((row) => [row.key, row.enabled === true]));
    return {
      adminQueue: byKey.get(CONTINUING_REVIEW_FLAGS.adminQueue) ?? false,
      userCta: byKey.get(CONTINUING_REVIEW_FLAGS.userCta) ?? false,
      creation: byKey.get(CONTINUING_REVIEW_FLAGS.creation) ?? false,
      reminders: byKey.get(CONTINUING_REVIEW_FLAGS.reminders) ?? false,
      billing: byKey.get(CONTINUING_REVIEW_FLAGS.billing) ?? false,
    };
  } catch (err) {
    console.error('Unexpected error loading continuing review flags:', err);
    return closed;
  }
};
